/**
 * Development Brand Selector Context
 *
 * Provides a brand selector modal for localhost development.
 * This allows developers to test different brands without changing hostnames.
 *
 * The selected brand is stored in localStorage and injected as X-Brand-Override
 * header in all API requests via getDevBrandSlug().
 *
 * Only activates when running on localhost (development mode).
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import {
  spacing,
  borderRadius,
  typography,
  colors,
} from '../constants/designTokens';

const STORAGE_KEY = 'dev-brand-slug';

const AVAILABLE_BRANDS = [
  { slug: 'safepassage', name: 'SafePassage', color: '#2563eb' },
  { slug: 'privateav', name: 'PrivateAV', color: '#1E3B78' },
  // Future brands can be added here
];

const getLocalStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readStoredBrandSlug = (): string | null => {
  const storage = getLocalStorage();
  if (typeof storage?.getItem !== 'function') {
    return null;
  }

  try {
    return storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredBrandSlug = (slug: string) => {
  const storage = getLocalStorage();
  if (typeof storage?.setItem !== 'function') {
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, slug);
  } catch {
    // Ignore storage errors in restricted browser contexts.
  }
};

interface DevBrandContextValue {
  brandSlug: string | null;
  selectBrand: (slug: string) => void;
}

const DevBrandContext = createContext<DevBrandContextValue | null>(null);

export function useDevBrand() {
  const ctx = useContext(DevBrandContext);
  if (!ctx) {
    throw new Error('useDevBrand must be used within DevBrandProvider');
  }
  return ctx;
}

export function DevBrandProvider({ children }: { children: ReactNode }) {
  const [brandSlug, setBrandSlug] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  useEffect(() => {
    if (!isDev) return;

    // Check for brand override from URL parameter (from SDK tester redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const urlBrand = urlParams.get('devBrand');
    if (urlBrand) {
      // Store in localStorage for subsequent requests
      writeStoredBrandSlug(urlBrand);
      setBrandSlug(urlBrand);
      return;
    }

    // Fall back to stored brand
    const stored = readStoredBrandSlug();
    if (stored) {
      setBrandSlug(stored);
    } else {
      setShowModal(true);
    }
  }, [isDev]);

  const selectBrand = (slug: string) => {
    writeStoredBrandSlug(slug);
    setBrandSlug(slug);
    setShowModal(false);
    window.location.reload();
  };

  if (!isDev) return <>{children}</>;

  // Custom modal that cannot be dismissed without selection
  const modal = showModal ? (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: colors.background.secondary,
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          maxWidth: '400px',
          width: '90%',
        }}
      >
        <h2
          style={{
            color: colors.text.primary,
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.semibold,
            marginBottom: spacing.sm,
            marginTop: 0,
            textAlign: 'center',
          }}
        >
          Select Development Brand
        </h2>
        <p
          style={{
            color: colors.text.secondary,
            fontSize: typography.fontSize.sm,
            marginBottom: spacing.lg,
            textAlign: 'center',
          }}
        >
          Choose a brand to test on localhost. This selection persists across
          page reloads.
        </p>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}
        >
          {AVAILABLE_BRANDS.map((brand) => (
            <button
              key={brand.slug}
              onClick={() => selectBrand(brand.slug)}
              style={{
                padding: spacing.md,
                backgroundColor: colors.background.tertiary,
                border: `2px solid ${brand.color}`,
                borderRadius: borderRadius.md,
                color: colors.text.primary,
                cursor: 'pointer',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                transition: 'background-color 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = brand.color;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor =
                  colors.background.tertiary;
              }}
            >
              {brand.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <DevBrandContext.Provider value={{ brandSlug, selectBrand }}>
      {children}
      {modal}
    </DevBrandContext.Provider>
  );
}

/**
 * Get the development brand slug for API header injection.
 * Works outside of React context - reads directly from localStorage.
 *
 * @returns The brand slug if on localhost and a brand is selected, null otherwise
 */
export function getDevBrandSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const isDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  if (!isDev) return null;
  return readStoredBrandSlug();
}
